import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { GetBannersParams } from '../../__generated__/gateway/gatewayApiTypes';
import { decorateBanners } from '../../helpers/decorateBanners';
import { getBanners } from '../../services/advertising';
import type { DecorateParams, ProcessStatusMap, TBannersList, TBannerTypes } from '../../types/advertising';

export interface IState {
  banners: TBannersList;
  loadingState: ProcessStatusMap;
}

const initialState: IState = {
  banners: null,
  loadingState: {},
};

export type FetchParams = {
  fetchingParams: GetBannersParams;
  decorateParams: DecorateParams;
};

export const fetchBanners = createAsyncThunk<TBannersList, FetchParams>(
  `advertising/fetchBanners`,
  async ({ fetchingParams }) => getBanners(fetchingParams),
);

const advertisingSlice = createSlice({
  name: 'advertising',
  initialState,
  reducers: {
    setBanners: (
      state,
      {
        payload,
      }: PayloadAction<{
        banners: TBannersList;
        decorateParams: DecorateParams;
      }>,
    ) => {
      const { banners, decorateParams } = payload;

      state.banners = decorateBanners(banners, decorateParams);

      state.loadingState = Object.keys(banners).reduce(
        (acc, type: TBannerTypes) => ({
          ...acc,
          [type]: false,
        }),
        {} as ProcessStatusMap,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBanners.fulfilled, (state, { payload, meta }) => {
      const { fetchingParams, decorateParams } = meta.arg;
      state.banners = {
        ...state.banners,
        ...decorateBanners(payload, decorateParams),
      };

      state.loadingState = fetchingParams.BannerTypes.reduce(
        (acc, type: TBannerTypes) => ({
          ...acc,
          [type]: false,
        }),
        state.loadingState,
      );
    });
    builder.addCase(fetchBanners.pending, (state, { meta }) => {
      const { fetchingParams } = meta.arg;

      state.loadingState = fetchingParams.BannerTypes.reduce(
        (acc, type: TBannerTypes) => ({
          ...acc,
          [type]: true,
        }),
        state.loadingState,
      );
    });
    builder.addCase(fetchBanners.rejected, (state, { meta }) => {
      const { fetchingParams } = meta.arg;

      state.loadingState = fetchingParams.BannerTypes.reduce(
        (acc, type: TBannerTypes) => ({
          ...acc,
          [type]: false,
        }),
        state.loadingState,
      );
    });
  },
});

const { actions, reducer } = advertisingSlice;

export const { setBanners } = actions;

export default reducer;
